import { MasterdataCustomer } from '@softapps/core/masterdata';
import { IsoTime } from '@softline/core';
import { DateRangeInputValue } from '@softline/ui-core/date';
import { VehicleAllocationKey } from './select-options';

/** Manuelle Zahlungskondition in the form — all fields editable/nullable;
    the request requires days1/days2/dueDays, incomplete entries are not sent. */
export interface ManualTermsOfPaymentFormValue {
  cashDiscount1: number | null;
  cashDiscount2: number | null;
  days1: number | null;
  days2: number | null;
  dueDays: number | null;
}

/** Structured address in the form — content only. Used for BOTH the manual
    Kundenadresse (`address`) and the manual Lieferadresse (`deliveryAddress`). */
export interface AddressFormValue {
  salutation: string | null;
  name1: string | null;
  name2: string | null;
  name3: string | null;
  street: string | null;
  countryCode: string | null;
  postCode: string | null;
  city: string | null;
  mail1: string | null;
}

export type DocumentFormData = {
  // Customer
  customer: MasterdataCustomer | null;
  /** Picked Baustelle's id (the Sub-Kunde row's `id` returned by the
   *  field-ok). The base customer object stays untouched — this id is
   *  used at PATCH time so the request ships
   *  `customerId: <Sub-Kunde id>` while the chip still shows the base
   *  customer. `null` means no Baustelle is currently picked. */
  constructionSiteId: number | null;
  /** Manual (free-text) Baustelle. Mutually exclusive with
   *  `constructionSiteId`. */
  constructionSite: string | null;
  /** Display name of the picked Baustelle (Sub-Kunde), captured at pick time.
   *  Display-only — never sent; the request carries `constructionSiteId`. */
  constructionSiteName: string | null;
  /** Whether the user opted into a manual (free-text) Baustelle. When false the
   *  Sub-Kunde picker is shown and `constructionSiteId` is used; when true the
   *  free-text `constructionSite` is used instead. */
  manualConstructionSiteEnabled: boolean;
  /** Read-only formatted address of the picked Baustelle (Sub-Kunde),
   *  captured at pick time from the field-ok row. Display-only — there is no
   *  backend field for it yet; shown in the Baustelle-address field and, when
   *  the manual-Baustelle toggle is on, folded into `manualAddress`. */
  constructionSiteAddress: string | null;
  /** Structured manual Baustellenadresse override, shown when
   *  `manualConstructionSiteEnabled` is on. No structured backend field exists
   *  yet for it — folded into `manualDeliveryAddress` as text at request time
   *  via `addressFormValueToText`. */
  constructionSiteAddressValue: AddressFormValue | null;
  /** Whether the user opted into a manual Kundenadresse. When false the address
   *  is not sent and the backend uses the customer's master-data address. */
  manualCustomerAddressEnabled: boolean;
  /** Structured manual Kundenadresse — sent as `address`. The backend has always
   *  returned it field by field; only the form used to flatten it to text. */
  manualAddressValue: AddressFormValue | null;
  /** Free-text manual LIEFERadresse (`manualDeliveryAddress`, kopfvk.lieferadresse
   *  — e.g. "hinterm Obi"). Despite the name this was never the Kundenadresse;
   *  it round-trips and is also fed by a manual Baustellenadresse. */
  manualAddressEnabled: boolean;
  manualAddress: string | null;
  header: string | null;
  footer: string | null;
  // Document
  documentDate: string | null;
  representative: object | null;
  representative2: object | null;
  siteEngineer: object | null;
  validUntil: string | null;
  /** Kennzeichen fields hold the backend option KEY (see select-options.ts);
   *  display names come from the option lists at render time. */
  taxTreatment: string | null; // In-/Ausland — kzinausland
  taxCalculation: string | null; // USt-Berechnung — kzmwst
  paymentInterval: string | null; // Periodenart — kzperiodenart
  attention: string | null;
  /** Leistungszeitraum (service period). `{ from, to }` with independently
   *  nullable endpoints; `null` when neither endpoint is set. */
  serviceDate: DateRangeInputValue | null;
  object: object | null;
  /** Kostenstelle as the picked masterdata record. Only its `name` is sent
   *  (`costCenterId` — the backend uses the name as the id), but the object is
   *  kept so the field still shows a label after a reload. */
  costCenter: object | null;
  stock: object | null; // Lager
  storageSite: object | null; // Lagerort
  country: object | null; // Land
  externalOrderNumber: string | null; // Externe Bestellnummer
  externalOrderDate: string | null; // Externes Bestelldatum
  /** Anwender — response-only display value (key + name), never sent back. */
  userName: string | null;
  note: string | null; // Vermerk (Intern, am Beleg)
  // Delivery
  deliveryType: string | null;
  /** "Nummer · Bezeichnung" of the picked Lieferart for display (stepper).
   *  Display-only — `deliveryType` stays the plain name because the pickup
   *  detection matches on it. */
  deliveryTypeLabel: string | null;
  distributionType: object | null;
  distributionTypeId: number | null;
  deliveryDateFrom: string | null;
  deliveryDateTo: string | null;
  calendarWeek: number | null;
  deliveryTime: IsoTime | null;
  transportMeans: string | null;
  deliveryNote: string | null;
  /** Fahrzeugeinteilung key; `null` = keine Einteilung. */
  vehicleAllocation: VehicleAllocationKey | null;
  driver: object | null;
  vehicle: object | null;
  /** Structured Lieferadresse. When the manual toggle is off the backend keeps
   *  the customer's delivery address and nothing is sent. */
  deliveryAddressManualEnabled: boolean;
  deliveryAddress: AddressFormValue | null;
  // Payment
  paymentCondition: object | null;
  paymentMethod: object | null;
  approvalForInvoicing: boolean; // Fakturenfreigabe
  /** Manuelle Zahlungskondition — mutually exclusive with `paymentCondition`
   *  (a picked Stammdaten-Kondition); the toggle decides which one is sent. */
  manualTermsOfPaymentEnabled: boolean;
  manualTermsOfPayment: ManualTermsOfPaymentFormValue | null;
  // Pickup
  pickupType: string | null;
  collector: string | null;
  pickupNote: string | null;
};

export const EMPTY_DOCUMENT_FORM_DATA: DocumentFormData = {
  customer: null,
  constructionSiteId: null,
  constructionSite: null,
  constructionSiteName: null,
  manualConstructionSiteEnabled: false,
  constructionSiteAddress: null,
  constructionSiteAddressValue: null,
  manualCustomerAddressEnabled: false,
  manualAddressValue: null,
  manualAddressEnabled: false,
  manualAddress: null,
  header: null,
  footer: null,
  documentDate: null,
  representative: null,
  representative2: null,
  siteEngineer: null,
  validUntil: null,
  taxTreatment: null,
  taxCalculation: null,
  paymentInterval: null,
  attention: null,
  serviceDate: null,
  object: null,
  costCenter: null,
  stock: null,
  storageSite: null,
  country: null,
  externalOrderNumber: null,
  externalOrderDate: null,
  userName: null,
  note: null,
  deliveryType: null,
  deliveryTypeLabel: null,
  distributionType: null,
  distributionTypeId: null,
  deliveryDateFrom: null,
  deliveryDateTo: null,
  calendarWeek: null,
  deliveryTime: null,
  transportMeans: null,
  deliveryNote: null,
  vehicleAllocation: null,
  driver: null,
  vehicle: null,
  deliveryAddressManualEnabled: false,
  deliveryAddress: null,
  paymentCondition: null,
  paymentMethod: null,
  approvalForInvoicing: false,
  manualTermsOfPaymentEnabled: false,
  manualTermsOfPayment: null,
  pickupType: null,
  collector: null,
  pickupNote: null,
};
